import React from "react";
import { Button } from "antd";
import { WarningOutlined } from "@ant-design/icons";

/**
 * Contains a render crash to one region instead of unmounting the app.
 *
 * Written after a stack overflow in the workflow layout blanked the whole Master Suite
 * builder and took the user's unsaved steps with it. The layout bug itself is fixed, but a
 * canvas is a lot of geometry running on every keystroke over data that is mid-edit by
 * definition — the next mistake in there should cost a panel, not an afternoon's work.
 *
 * Deliberately generic and free of app state: it is wrapped AROUND the thing that might
 * fail, so the editor state lives in the parent and survives untouched. The user can keep
 * working in the Table view and still save.
 *
 * Props:
 *   title     string  — what failed, in the user's terms
 *   hint      string  — what they can still do
 *   children  node
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // Keep the stack — the fallback deliberately shows the user nothing technical.
    console.error("ErrorBoundary caught:", error, info?.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="arc-boundary">
        <WarningOutlined className="arc-boundary-icon" />
        <p className="arc-boundary-title">
          {this.props.title || "This section couldn't be displayed"}
        </p>
        {this.props.hint && <p className="arc-boundary-hint">{this.props.hint}</p>}
        <Button size="small" onClick={() => this.setState({ failed: false })}>
          Try again
        </Button>
      </div>
    );
  }
}

export default ErrorBoundary;
